import React, { memo, useState, useEffect, useCallback, useRef } from 'react';
import { GlossaryEntry, ScoreStatistics } from '@/lib/api';
import { ScoreInput } from './ScoreInput';
import { calculateRawValueFromScore, calculateScoreFromRawValue } from './utils';
import { ScoreComparison } from '../ScoreComparison';

// Debounced Slider Component to prevent UI lag
const DebouncedScoreSlider = ({
    value,
    min,
    max,
    step,
    disabled,
    onChange,
    getScoreColor
}: {
    value: number;
    min: string;
    max: string;
    step: string;
    disabled: boolean;
    onChange: (val: number) => void;
    getScoreColor: (s: number | null) => string;
}) => {
    const [localValue, setLocalValue] = useState(value);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Sync with external value ensuring we don't overwrite while user is dragging if we track that,
    // but simplified: sync when prop changes.
    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newVal = parseFloat(e.target.value);
        setLocalValue(newVal);

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        // Debounce update to parent to keep UI smooth
        timeoutRef.current = setTimeout(() => {
            onChange(newVal);
        }, 16); // ~1 frame delay
    };

    return (
        <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={localValue}
            disabled={disabled}
            onChange={handleChange}
            className={`flex-1 h-2 appearance-none cursor-pointer ${getScoreColor(localValue)}`}
            style={{
                background: !disabled
                    ? `linear-gradient(to right, #ef4444 0%, #f97316 25%, #eab308 50%, #84cc16 75%, #22c55e 100%)`
                    : '#e2e8f0'
            }}
        />
    );
};

interface SubcategoryRowProps {
    categoryName: string;
    sub: { name: string; unit?: string; scores: Record<string, string> };
    currentScore: { score: number | null; reliability: number; raw_value: string | null };
    subDefinition: GlossaryEntry | null;
    t: any;
    getScoreColor: (s: number | null) => string;
    onScoreChange: (cat: string, sub: string, score: number | null | undefined, rel?: number, raw?: string | null) => void;
    stats?: ScoreStatistics | null;
    weight: number;
    isReadOnly?: boolean;
    isFirst?: boolean;
}

export const SubcategoryRow = memo(({
    categoryName,
    sub,
    currentScore,
    subDefinition,
    t,
    getScoreColor,
    onScoreChange,
    stats,
    weight,
    isReadOnly = false,
    isFirst = false,
}: SubcategoryRowProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const isWeightZero = weight === 0;
    const isInputDisabled = isWeightZero || isReadOnly;

    const handleSliderChange = useCallback((newScore: number) => {
        if (isInputDisabled) return;

        // Calculate corresponding raw value from score
        let newRawValue: string | null = null;
        if (Object.keys(sub.scores).length > 0) {
            const calculatedRaw = calculateRawValueFromScore(newScore, sub.scores);
            if (calculatedRaw !== null) {
                newRawValue = calculatedRaw.toString();
            }
        }
        onScoreChange(
            categoryName,
            sub.name,
            newScore,
            undefined,
            newRawValue !== null ? newRawValue : undefined
        );
    }, [categoryName, sub.name, sub.scores, isInputDisabled, onScoreChange]);

    return (
        <div className={`p-4 hover:bg-slate-50/30 relative overflow-visible bg-transparent ${isWeightZero ? 'opacity-75 grayscale-[25%]' : ''}`}>
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                {/* Subcategory info - clickable title */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h4
                            className={`font-medium text-black text-sm ${!isWeightZero ? 'cursor-pointer hover:text-violet-600' : ''} transition-colors`}
                            onClick={() => !isWeightZero && setIsExpanded(!isExpanded)}
                        >
                            {sub.name}
                        </h4>
                        {/* Show No Weight badge if disabled */}
                        {isWeightZero && (
                            <span className="text-[10px] uppercase font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 border border-slate-200">
                                No Weight
                            </span>
                        )}
                        {!isWeightZero && subDefinition && (
                            <div className="group relative z-20 hover:z-50">
                                <svg className="w-4 h-4 text-slate-400 hover:text-violet-500 transition-colors cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <div className="absolute left-0 top-6 z-[9999] hidden group-hover:block w-80 p-3 bg-slate-800 text-white text-xs shadow-xl border border-slate-700 whitespace-normal break-words">
                                    <div className="font-semibold mb-1">{subDefinition.terminology}</div>
                                    <div>{subDefinition.definition}</div>
                                </div>
                            </div>
                        )}
                        {/* Score Comparison */}
                        {!isWeightZero && stats && (
                            <ScoreComparison userScore={currentScore.score} stats={stats} compact />
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {sub.unit && (
                            <span className="text-xs text-slate-400">{sub.unit}</span>
                        )}
                        {/* Current score description (beside unit) - clickable */}
                        {!isWeightZero && currentScore.score !== null && Object.keys(sub.scores).length > 0 && (() => {
                            const score = currentScore.score!;
                            const scoreEntries = Object.entries(sub.scores);

                            for (const [level, desc] of scoreEntries) {
                                const match = level.match(/^(\d+)/);
                                if (match) {
                                    const scoreLevel = parseInt(match[1]);
                                    if (Math.round(score) === scoreLevel) {
                                        return (
                                            <span
                                                className="text-xs text-slate-400 cursor-pointer hover:text-violet-600 transition-colors ml-2"
                                                onClick={() => setIsExpanded(!isExpanded)}
                                            >
                                                {desc}
                                            </span>
                                        );
                                    }
                                }
                            }
                            return null;
                        })()}
                    </div>
                </div>


                {/* Raw value input (calculates score from ranges) - NOW ON LEFT */}
                <div className="lg:w-[120px]">
                    {isFirst && <div className="hidden lg:block text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">{t('unit')}</div>}
                    {(() => {
                        if (isWeightZero) {
                            return (
                                <div className="w-full px-2 py-1 text-center text-sm text-slate-400 border border-slate-100 bg-slate-50 italic">
                                    N/A
                                </div>
                            );
                        }

                        // Calculate display value: use raw_value if set, otherwise calculate from score
                        let displayValue = currentScore.raw_value || '';
                        if (displayValue === '' && currentScore.score !== null && Object.keys(sub.scores).length > 0) {
                            const calculatedRaw = calculateRawValueFromScore(currentScore.score, sub.scores);
                            if (calculatedRaw !== null) {
                                displayValue = calculatedRaw.toString();
                            } else {
                                // For qualitative subcategories where calculation isn't possible, show the score itself
                                displayValue = currentScore.score.toString();
                            }
                        } else if (displayValue === '' && currentScore.score !== null) {
                            // No score ranges defined, show the score as the raw value
                            displayValue = currentScore.score.toString();
                        }
                        return (
                            <input
                                type="number"
                                step="any"
                                value={displayValue}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    const rawValue = val === '' ? null : val;

                                    // Calculate score from raw value using subcategory ranges
                                    let calculatedScore: number | null = null;
                                    if (val !== '' && Object.keys(sub.scores).length > 0) {
                                        const num = parseFloat(val);
                                        if (!isNaN(num)) {
                                            calculatedScore = calculateScoreFromRawValue(num, sub.scores);
                                        }
                                    }

                                    onScoreChange(
                                        categoryName,
                                        sub.name,
                                        calculatedScore,
                                        undefined,
                                        rawValue
                                    );
                                }}
                                placeholder={sub.unit || t('value')}
                                disabled={isInputDisabled}
                                className={`w-full px-2 py-1 text-center text-sm border focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${isInputDisabled ? 'bg-slate-50 text-slate-500 border-slate-200 cursor-not-allowed' : 'text-black border-slate-200'}`}
                            />
                        );
                    })()}
                </div>

                {/* Score slider with editable display - NOW IN MIDDLE */}
                <div className="flex items-center gap-2 lg:w-[240px]">
                    <div className="flex flex-col flex-1">
                        {isFirst && <div className="hidden lg:block mb-1 text-[10px] opacity-0 select-none">_</div>}
                    <DebouncedScoreSlider
                        min="0"
                        max="5"
                        step="0.1"
                        value={currentScore.score ?? 0}
                        disabled={isInputDisabled}
                        onChange={handleSliderChange}
                        getScoreColor={getScoreColor}
                    />
                    </div>
                    <div className="flex flex-col items-center">
                        {isFirst && <div className="hidden lg:block text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">{t('score')}</div>}
                        <ScoreInput
                            min={0}
                            max={5}
                            step="0.1"
                            value={currentScore.score}
                            disabled={isInputDisabled}
                            onChange={(val) => {
                                if (isInputDisabled) return;
                                if (val === null) {
                                    onScoreChange(categoryName, sub.name, null, undefined, null);
                                    return;
                                }

                                // Round to 1 decimal
                                const newScore = Math.round(val * 10) / 10;

                                // Calculate corresponding raw value from score
                                let newRawValue: string | null = null;
                                if (Object.keys(sub.scores).length > 0) {
                                    const calculatedRaw = calculateRawValueFromScore(newScore, sub.scores);
                                    if (calculatedRaw !== null) {
                                        newRawValue = calculatedRaw.toString();
                                    }
                                }

                                onScoreChange(
                                    categoryName,
                                    sub.name,
                                    newScore,
                                    undefined,
                                    newRawValue !== null ? newRawValue : undefined
                                );
                            }}
                            className={`w-16 px-2 py-1 text-center text-sm font-medium border focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${isInputDisabled ? 'bg-slate-50 text-slate-500 border-slate-200 cursor-not-allowed' : 'text-black border-slate-200'}`}
                        />
                    </div>
                </div>

                {/* Reliability selector */}
                <div className="flex flex-col gap-0.5">
                    {isFirst && <div className="hidden lg:block text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">{t('reliability')}</div>}
                    <div className="flex items-center bg-slate-100 p-0.5 rounded">
                        {[0.5, 0.75, 1.0].map((rel) => (
                            <button
                                key={rel}
                                title={t('reliability')}
                                disabled={isInputDisabled}
                                onClick={() => !isInputDisabled && onScoreChange(
                                    categoryName,
                                    sub.name,
                                    undefined,  // Don't change the score, only reliability
                                    rel
                                )}
                                className={`px-2 py-1 text-xs font-medium transition-all duration-200 rounded-sm cursor-pointer ${currentScore.reliability === rel && !isInputDisabled
                                    ? 'bg-white text-slate-800 shadow-sm'
                                    : isInputDisabled
                                        ? 'text-slate-300 cursor-not-allowed'
                                        : 'text-slate-500 hover:text-slate-700'
                                    }`}
                            >
                                {Math.round(rel * 100)}%
                            </button>
                        ))}
                    </div>
                </div>
            </div>



            {/* Expandable scale reference ranges */}
            {Object.keys(sub.scores).length > 0 && isExpanded && !isWeightZero && (
                <div
                    className="mt-2 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => setIsExpanded(false)}
                >
                    <div className="flex gap-1 flex-wrap">
                        {Object.entries(sub.scores).map(([level, desc]) => (
                            <span
                                key={level}
                                className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-black whitespace-nowrap"
                            >
                                {level}: {desc}
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
});

SubcategoryRow.displayName = 'SubcategoryRow';
